import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SendIcon from "@mui/icons-material/Send";

export interface AudienceBreakdown {
  methodOfOptin: string;
  name: string;
  id: string;
  count: number;
}

export interface ExclusionCampaignBreakdown {
  campaignName: string;
  campaignId: string;
  excludedCount: number;
}

export interface ConfirmationData {
  totalNoOfEffectiveAudiences: number;
  selectedAudiencesList: {
    totalNoOfAudiencesSelected: number;
    audiencesBreakdown: AudienceBreakdown[];
  };
  selectedExclusionCampaignsList: {
    totalNoOfExcludedAudiences: number;
    exclusionCampaignsBreakdown: ExclusionCampaignBreakdown[];
  };
  statusCode: string;
}

interface MessageConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  confirmData: ConfirmationData | null;
  recipientCountError?: boolean;
  excludedCampaignsCount: number;
}

const MessageConfirmDialog: React.FC<MessageConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
  confirmData,
  recipientCountError = false,
  excludedCampaignsCount,
}) => {
  const recipientCount = confirmData?.totalNoOfEffectiveAudiences ?? 0;
  const audiencesBreakdown =
    confirmData?.selectedAudiencesList?.audiencesBreakdown ?? [];
  const totalAudiencesSelected =
    confirmData?.selectedAudiencesList?.totalNoOfAudiencesSelected ?? 0;
  const exclusionBreakdown =
    confirmData?.selectedExclusionCampaignsList?.exclusionCampaignsBreakdown ??
    [];
  const totalExcluded =
    confirmData?.selectedExclusionCampaignsList?.totalNoOfExcludedAudiences ??
    0;
  const allRecipientsExcluded =
    confirmData !== null &&
    !recipientCountError &&
    recipientCount === 0 &&
    excludedCampaignsCount > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      slotProps={{
        paper: { sx: { maxWidth: "38%", maxHeight: "100%" } },
      }}>
      <DialogTitle sx={{ textAlign: "center", fontSize: "1.5rem" }}>
        Confirm Message Send
      </DialogTitle>
      <DialogContent
        dividers
        className="custom-scrollbar-css"
        sx={{ pt: 2, height: "350px", overflowY: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}>
          <SendIcon fontSize="medium" color="primary" />
          <Typography
            variant="body1"
            sx={{ fontSize: "1.2rem", fontWeight: 500 }}>
            You're about to send this message to
          </Typography>
          {loading ? (
            <Skeleton variant="rounded" width={60} height={32} />
          ) : (
            <Chip
              label={recipientCountError ? "--" : recipientCount}
              color="primary"
              size="medium"
            />
          )}
          <Typography
            variant="body1"
            sx={{ fontSize: "1.2rem", fontWeight: 500 }}>
            users.
          </Typography>
        </Box>

        {recipientCountError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Recipient count could not be loaded. You can still send — click
            Confirm to proceed.
          </Alert>
        )}

        {allRecipientsExcluded && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            All recipients were already reached by the excluded campaigns.
          </Alert>
        )}

        <Box sx={{ mt: 4 }}>
          <Accordion>
            <AccordionSummary
              expandIcon={<ArrowDownwardIcon />}
              aria-controls="audiences-content"
              id="audiences-header">
              <Typography component="span" sx={{ width: "84%", flexShrink: 0 }}>
                Total Audiences Selected
              </Typography>
              <Typography>
                {loading ? (
                  <Skeleton variant="rounded" width={40} height={24} />
                ) : (
                  <Chip
                    label={recipientCountError ? "--" : totalAudiencesSelected}
                    size="small"
                    sx={{ bgcolor: "#5ac8fa", color: "#fff" }}
                  />
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
              {loading ? (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Skeleton variant="rounded" width="30%" height={32} />
                  <Skeleton variant="rounded" width="30%" height={32} />
                  <Skeleton variant="rounded" width="30%" height={32} />
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {audiencesBreakdown.map((item, i) => (
                    <Box key={i} sx={{ mr: 2, mb: 1 }}>
                      <Badge
                        badgeContent={recipientCountError ? "?" : item.count}
                        max={Number.MAX_SAFE_INTEGER}
                        sx={{
                          "& .MuiBadge-badge": {
                            bgcolor: "#5ac8fa",
                            color: "#fff",
                          },
                        }}>
                        <Chip label={item.name} variant="outlined" />
                      </Badge>
                    </Box>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>

        {excludedCampaignsCount > 0 && (
          <Box sx={{ mt: 4 }}>
            <Accordion>
              <AccordionSummary
                expandIcon={<ArrowDownwardIcon />}
                aria-controls="exclusions-content"
                id="exclusions-header">
                <Typography
                  component="span"
                  sx={{ width: "84%", flexShrink: 0 }}>
                  Total Excluded Campaigns
                </Typography>
                <Typography>
                  {loading ? (
                    <Skeleton variant="rounded" width={40} height={24} />
                  ) : (
                    <Chip
                      label={recipientCountError ? "--" : totalExcluded}
                      size="small"
                      sx={{ bgcolor: "#f78f66f5", color: "#fff" }}
                    />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1 }}>
                {loading ? (
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <Skeleton variant="rounded" width="30%" height={32} />
                    <Skeleton variant="rounded" width="30%" height={32} />
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                    {exclusionBreakdown.map((item, i) => (
                      <Box key={i} sx={{ mr: 2, mb: 1 }}>
                        <Badge
                          badgeContent={
                            recipientCountError ? "?" : item.excludedCount
                          }
                          max={Number.MAX_SAFE_INTEGER}
                          sx={{
                            "& .MuiBadge-badge": {
                              bgcolor: "#f78f66f5",
                              color: "#fff",
                            },
                          }}>
                          <Chip label={item.campaignName} variant="outlined" />
                        </Badge>
                      </Box>
                    ))}
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={!recipientCountError && allRecipientsExcluded}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessageConfirmDialog;
